import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PriceChart from '@/components/feature/PriceChart';
import { goldTypes } from '@/mocks/goldPrices';

const timeOptions = [
  { seconds: 180, profit: 5, label: '180s / 5%' },
  { seconds: 300, profit: 10, label: '300s / 10%' },
  { seconds: 540, profit: 15, label: '540s / 15%' },
  { seconds: 720, profit: 20, label: '720s / 20%' },
];

const mockBalance = 2624945750;

export default function Trading() {
  const [amount, setAmount] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState(timeOptions[0]);
  const [goldId, setGoldId] = useState(goldTypes[0].id);
  const [toast, setToast] = useState<string | null>(null);
  const [pendingOrder, setPendingOrder] = useState<null | { side: 'up' | 'down'; amount: number; profit: number; seconds: number }>(null);
  const [recentOrders, setRecentOrders] = useState([
    { time: '14:30:03', side: 'Mua', amount: '1.5 lượng', price: 144000000 },
    { time: '14:29:58', side: 'Bán', amount: '0.5 lượng', price: 143800000 },
    { time: '14:29:41', side: 'Mua', amount: '2 lượng', price: 143600000 },
    { time: '14:29:22', side: 'Mua', amount: '1 lượng', price: 143400000 },
    { time: '14:29:05', side: 'Bán', amount: '3 lượng', price: 143200000 },
  ]);

  const selected = goldTypes.find((g) => g.id === goldId) ?? goldTypes[0];

  const finalProfit = useMemo(() => {
    const num = Number(amount.replace(/\./g, '').replace(/,/g, ''));
    if (!num || num <= 0) return 0;
    return Math.round(num * (selectedTime.profit / 100));
  }, [amount, selectedTime]);

  const totalAmount = useMemo(() => {
    const num = Number(amount.replace(/\./g, '').replace(/,/g, ''));
    return !num || num <= 0 ? 0 : num;
  }, [amount]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) { setAmount(''); return; }
    setAmount(Number(raw).toLocaleString('vi-VN'));
  };

  const handleTrade = (side: 'up' | 'down') => {
    if (totalAmount <= 0) {
      setToast('Vui lòng nhập số tiền giao dịch');
      setTimeout(() => setToast(null), 2500);
      return;
    }
    setPendingOrder({ side, amount: totalAmount, profit: finalProfit, seconds: selectedTime.seconds });
  };

  const confirmTrade = () => {
    if (!pendingOrder) return;
    const now = new Date();
    const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
    const sideLabel = pendingOrder.side === 'up' ? 'Mua lên' : 'Mua xuống';
    setRecentOrders((prev) => [
      { time: timeStr, side: sideLabel, amount: `${pendingOrder.amount.toLocaleString('vi-VN')}đ`, price: selected.buy },
      ...prev.slice(0, 4),
    ]);
    setToast(`${sideLabel} ${pendingOrder.amount.toLocaleString('vi-VN')}đ — ${pendingOrder.seconds}s / +${pendingOrder.profit.toLocaleString('vi-VN')}đ`);
    setTimeout(() => setToast(null), 3000);
    setPendingOrder(null);
    setAmount('');
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="mx-auto max-w-6xl px-4 md:px-6 py-8">
          <div className="flex items-center gap-2 text-sm text-foreground-600 mb-6">
            <Link to="/" className="hover:text-primary-300 cursor-pointer">Trang chủ</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-900">Giao dịch</span>
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6">Giao dịch vàng</h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <PriceChart />

              <div className="rounded-xl border border-background-200/60 bg-background-100 p-4 md:p-5">
                <h3 className="text-sm font-semibold text-foreground-950 mb-4">Sổ lệnh gần đây</h3>
                <div className="space-y-2">
                  {recentOrders.map((o, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-background-200/40 last:border-0 text-sm">
                      <span className="text-foreground-600 whitespace-nowrap">{o.time}</span>
                      <span className={`font-semibold ${o.side === 'Mua' || o.side === 'Mua lên' ? 'text-emerald-500' : o.side === 'Mua xuống' ? 'text-rose-500' : 'text-rose-500'}`}>{o.side}</span>
                      <span className="text-foreground-800 whitespace-nowrap">{o.amount}</span>
                      <span className="font-semibold text-foreground-950 whitespace-nowrap">{o.price.toLocaleString('vi-VN')}đ</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-background-200/60 bg-background-950 p-5 h-fit text-foreground-50">
              <div className="mb-4">
                <label className="block text-xs font-semibold text-foreground-400 mb-1.5">Loại vàng</label>
                <select
                  value={goldId}
                  onChange={(e) => setGoldId(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-md bg-background-900 border border-background-700 text-sm text-foreground-50 focus:outline-none focus:border-primary-500"
                >
                  {goldTypes.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-end justify-between mb-1">
                <span className="text-xs font-semibold text-foreground-400">Số tiền (VND)</span>
                <span className="text-xs text-foreground-300">Tối đa <span className="font-semibold text-foreground-50">{mockBalance.toLocaleString('vi-VN')} VND</span></span>
              </div>
              <div className="relative mb-5">
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="Nhập số tiền (VND)"
                  value={amount}
                  onChange={handleAmountChange}
                  className="w-full px-3 py-3 rounded-md bg-background-900 border border-background-700 text-sm text-foreground-50 placeholder-foreground-500 focus:outline-none focus:border-primary-500"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-foreground-400 pointer-events-none">VND</span>
              </div>

              <p className="text-xs font-semibold text-foreground-400 mb-3">Thời lượng / lợi nhuận</p>
              <div className="grid grid-cols-2 gap-2 mb-5">
                {timeOptions.map((opt) => (
                  <button
                    key={opt.seconds}
                    onClick={() => setSelectedTime(opt)}
                    className={`py-2.5 rounded-lg text-xs font-bold whitespace-nowrap cursor-pointer transition-colors border ${
                      selectedTime.seconds === opt.seconds
                        ? 'bg-primary-500 text-background-950 border-primary-500'
                        : 'bg-transparent text-foreground-50 border-background-700 hover:border-background-500'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center justify-between mb-5">
                <span className="text-xs text-foreground-400">Lợi nhuận cuối cùng</span>
                <span className="text-sm font-bold text-foreground-50">{finalProfit.toLocaleString('vi-VN')} VND</span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleTrade('up')}
                  className="py-3 rounded-full font-bold text-sm whitespace-nowrap cursor-pointer transition-colors bg-emerald-500 text-background-950 hover:bg-emerald-400"
                >
                  MUA LÊN
                </button>
                <button
                  onClick={() => handleTrade('down')}
                  className="py-3 rounded-full font-bold text-sm whitespace-nowrap cursor-pointer transition-colors bg-rose-500 text-background-950 hover:bg-rose-400"
                >
                  MUA XUỐNG
                </button>
              </div>

              <p className="mt-4 text-xs text-foreground-500 text-center">Bạn cần đăng nhập để thực hiện giao dịch thực.</p>
            </div>
          </div>
        </div>
      </main>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-lg bg-background-950 text-foreground-50 text-sm font-semibold border border-background-700 animate-fade-in">
          {toast}
        </div>
      )}

      {pendingOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setPendingOrder(null)}>
          <div className="rounded-xl bg-background-100 p-6 max-w-sm w-full mx-4 border border-background-200/60" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-bold text-foreground-950 mb-4">Xác nhận lệnh</h3>
            <div className="space-y-2 mb-5 text-sm">
              <div className="flex justify-between">
                <span className="text-foreground-600">Loại lệnh</span>
                <span className={`font-bold ${pendingOrder.side === 'up' ? 'text-emerald-500' : 'text-rose-500'}`}>
                  {pendingOrder.side === 'up' ? 'MUA LÊN' : 'MUA XUỐNG'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-foreground-600">Số tiền</span>
                <span className="font-semibold text-foreground-950">{pendingOrder.amount.toLocaleString('vi-VN')}đ</span>
              </div>
              <div className="flex justify-between">
                <span className="text-foreground-600">Thời lượng</span>
                <span className="font-semibold text-foreground-950">{pendingOrder.seconds}s</span>
              </div>
              <div className="flex justify-between">
                <span className="text-foreground-600">Lợi nhuận dự kiến</span>
                <span className="font-semibold text-primary-500">+{pendingOrder.profit.toLocaleString('vi-VN')}đ</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => setPendingOrder(null)} className="py-2.5 rounded-md text-sm font-semibold cursor-pointer border border-background-300/60 text-foreground-700 hover:bg-background-200/50">Hủy</button>
              <button onClick={confirmTrade} className={`py-2.5 rounded-md text-sm font-semibold cursor-pointer text-background-950 ${pendingOrder.side === 'up' ? 'bg-emerald-500 hover:bg-emerald-400' : 'bg-rose-500 hover:bg-rose-400'}`}>Xác nhận</button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}